import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  Tabs,
  Tab,
} from '@material-ui/core';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { motion } from 'framer-motion';
import { UserContext } from './UserContext';
import axios from 'axios';
import { useMemo } from 'react';

axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('jwtToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 'calc(100vh - 95px)', // Adjusted style
  },
  buttonContainer: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  button: {
    whiteSpace: 'nowrap',
    backgroundColor: '#212121', // Set the background color to dark
    color: '#fff', // Set the text color to white
    '&:hover': {
      backgroundColor: '#212121', // Set the background color on hover
    },
  },
  tableContainer: {
    maxHeight: 'calc(100vh - 150px)', // Set the desired height for the table container
    width: '80%', // Set the desired width for the table container
    margin: '0 auto', // Center the table container horizontally
    overflowY: 'scroll', // Add scroll bars when table overflows
    '& th, & td': {
      border: '1px solid #dddddd', // Add border to table cells
      whiteSpace: 'nowrap', // Prevent text from wrapping
      overflow: 'hidden', // Hide overflowed content
      textOverflow: 'ellipsis',
    },
  },
  tableHeaderCell: {
    backgroundColor: '#212121', // Set the background color for the table header cells
    color: '#fff', // Set the text color to white
    fontWeight: 'bold',
    fontSize: '1rem',
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  pageNumber: {
    backgroundColor: '#fff', // Set background color for unselected page numbers
    color: '#212121', // Set text color for unselected page numbers
    minWidth: '32px', // Adjust as needed
    padding: '4px 8px', // Adjust as needed
    margin: '0 2px', // Adjust as needed
    cursor: 'pointer',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: '#eee', // Set the background color on hover
    },
  },
  selectedPageNumber: {
    backgroundColor: '#212121', // Set background color for selected page number
    color: '#fff', // Set text color for selected page number
    minWidth: '32px', // Adjust as needed
    padding: '4px 8px', // Adjust as needed
    margin: '0 2px', // Adjust as needed
    cursor: 'pointer',
    borderRadius: '4px',
  },
  tabsWrap: {
    width: '80%',
    margin: '0 auto 16px',
  },
  actionsRow: {
    width: '80%',
    margin: '8px auto 8px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

// ---- small CSV parser (safe for our simple export) ----
function parseCSV(text) {
  // very simple CSV (quoted, comma separated) parser good enough for our export
  const lines = text.trim().split(/\r?\n/);
  if (!lines.length) return [];
  const headers = lines[0].split(',').map((h) => h.replace(/^"|"$/g, ''));
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = [];
    let cur = '';
    let inQuotes = false;
    const line = lines[i];
    for (let j = 0; j < line.length; j++) {
      const ch = line[j];
      if (ch === '"') {
        if (inQuotes && line[j + 1] === '"') {
          cur += '"';
          j++; // escaped quote
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === ',' && !inQuotes) {
        cols.push(cur);
        cur = '';
      } else {
        cur += ch;
      }
    }
    cols.push(cur);
    const obj = {};
    headers.forEach(
      (h, idx) => (obj[h] = (cols[idx] || '').replace(/^"|"$/g, ''))
    );
    rows.push(obj);
  }
  return rows;
}

// ---- CSV helpers ----
const quote = (v) => {
  if (v === null || v === undefined) return '""';
  const s = String(v).replace(/"/g, '""');
  return `"${s}"`;
};

function buildCSV(rows, columns, columnLabels = null) {
  const header = (columnLabels || columns).map(quote).join(',');
  const lines = rows.map((r) =>
    columns
      .map((c) => quote(typeof c === 'function' ? c(r) : r[c] ?? ''))
      .join(',')
  );
  return [header, ...lines].join('\n');
}

function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const HomePage = () => {
  const classes = useStyles();
  const { user, logout } = useContext(UserContext); // Access the user object from UserContext

  // helper to format date + time consistently
  const formatDateTime = (val) => {
    if (!val) return '—';
    const d = new Date(val);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // tabs
  const [activeTab, setActiveTab] = useState('registrations'); // 'registrations' | 'leads'

  // registrations state
  const [regData, setRegData] = useState([]);
  const [regSortingOrder, setRegSortingOrder] = useState('desc');
  const [regCurrentPage, setRegCurrentPage] = useState(1);
  const [regRowsPerPage] = useState(10);
  const [regSearchQuery, setRegSearchQuery] = useState('');
  const [regTotalPages, setRegTotalPages] = useState(1);

  // leads state
  const [leadsData, setLeadsData] = useState([]); // page slice or full (csv fallback)
  const [leadsAll, setLeadsAll] = useState([]); // only used when CSV fallback
  const [leadsCurrentPage, setLeadsCurrentPage] = useState(1);
  const [leadsRowsPerPage] = useState(10);
  const [leadsTotalPages, setLeadsTotalPages] = useState(1);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const token = localStorage.getItem('jwtToken');
  // ---- API helpers ----
  const fetchRegistrations = useCallback(
    async (page = 1, search = '') => {
      if (!token) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/formdata`,
          { params: { page, limit: regRowsPerPage, search } }
        );
        const { data, totalPages } = response.data;
        setRegData(data || []);
        setRegTotalPages(totalPages || 1);
      } catch (error) {
        console.error('Registrations fetch error:', error);
        if (error.response?.status === 401 || error.response?.status === 403)
          logout();
      }
    },
    [regRowsPerPage, logout, token]
  );

  const fetchLeadsJSON = useCallback(
    async (page = 1) => {
      if (!token) return;
      setLeadsLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/gated-leads`,
          { params: { page, limit: leadsRowsPerPage } }
        );
        // expected: { data: [], totalPages }
        const { data, totalPages } = response.data || {};
        if (Array.isArray(data) && data.length >= 0) {
          setLeadsData(data);
          setLeadsTotalPages(totalPages || 1);
          setLeadsAll([]); // not using CSV fallback
          setLeadsLoading(false);
          return true;
        }
      } catch (e) {
        // likely 404 if JSON route not present -> fall back to CSV
      }
      setLeadsLoading(false);
      return false;
    },
    [leadsRowsPerPage, token]
  );

  const fetchLeadsCSV = useCallback(async () => {
    if (!token) return;
    setLeadsLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/gated-leads.csv`,
        { responseType: 'text' }
      );
      const rows = parseCSV(res.data);
      // normalize keys to match our table usage
      const normalized = rows.map((r) => ({
        id: r.id,
        email: r.email,
        phone: r.phone,
        intent: r.intent,
        consent:
          r.consent === '1' || r.consent === 'true' || r.consent === 'TRUE',
        createdAt: r.created_at || r.createdAt || r.createdAt, // depends on backend
      }));
      setLeadsAll(normalized);
      setLeadsTotalPages(
        Math.max(1, Math.ceil(normalized.length / leadsRowsPerPage))
      );
      setLeadsLoading(false);
    } catch (err) {
      console.error('Leads CSV fetch error:', err);
      setLeadsLoading(false);
    }
  }, [leadsRowsPerPage, token]);

  const handleDownloadLeadsCSV = async () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');

    if (activeTab !== 'leads') return;

    // 1) Try server CSV first
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/gated-leads.csv`,
        { responseType: 'blob' }
      );
      downloadBlob(`leads_${y}-${m}-${d}.csv`, res.data);
      return;
    } catch (_) {
      // Fallback to client-generated CSV
    }

    // 2) Fallback: build CSV from loaded leads in memory
    const rows = (leadsAll?.length ? leadsAll : leadsData) || [];
    const columns = ['id', 'email', 'phone', 'intent', 'consent', 'createdAt'];
    const labels = ['ID', 'Email', 'Phone', 'Intent', 'Consent', 'Created At'];
    const csv = buildCSV(rows, columns, labels);
    downloadBlob(
      `leads_${y}-${m}-${d}.csv`,
      new Blob([csv], { type: 'text/csv;charset=utf-8' })
    );
  };

  const regViewData = React.useMemo(() => {
    const copy = Array.isArray(regData) ? [...regData] : [];
    copy.sort((a, b) => {
      const aDate = new Date(a.createdAt);
      const bDate = new Date(b.createdAt);
      return regSortingOrder === 'asc' ? aDate - bDate : bDate - aDate;
    });
    return copy;
  }, [regData, regSortingOrder]);

  // ---- Effects ----
  useEffect(() => {
    if (!user) return;
    if (activeTab === 'registrations') {
      fetchRegistrations(regCurrentPage, regSearchQuery);
    }
  }, [user, activeTab, regCurrentPage, regSearchQuery, fetchRegistrations]);

  useEffect(() => {
    if (!user) return;
    if (activeTab === 'leads') {
      (async () => {
        // try JSON first; on failure, fall back to CSV
        const ok = await fetchLeadsJSON(leadsCurrentPage);
        if (!ok) {
          await fetchLeadsCSV();
        }
      })();
    }
  }, [user, activeTab, leadsCurrentPage, fetchLeadsJSON, fetchLeadsCSV]);

  // ---- Sorting / filtering (registrations only) ----
  const sortedRegData = useMemo(() => {
    const copy = Array.isArray(regData) ? [...regData] : [];
    copy.sort((a, b) => {
      const aDate = new Date(a.createdAt);
      const bDate = new Date(b.createdAt);
      return regSortingOrder === 'asc' ? aDate - bDate : bDate - aDate;
    });
    return copy;
  }, [regData, regSortingOrder]);

  // ---- Leads page slice when CSV fallback is used ----
  const leadsPageSlice = useMemo(() => {
    if (leadsAll.length === 0) return leadsData; // JSON mode
    const start = (leadsCurrentPage - 1) * leadsRowsPerPage;
    const end = start + leadsRowsPerPage;
    return leadsAll.slice(start, end);
  }, [leadsAll, leadsData, leadsCurrentPage, leadsRowsPerPage]);

  // ---- Pagination helpers ----
  const makePageNumbers = (total, current) => {
    const count = Math.max(1, total);
    const show = 5;
    const half = Math.floor(show / 2);
    let start = Math.max(1, current - half);
    let end = Math.min(count, start + show - 1);
    if (end - start + 1 < show) start = Math.max(1, end - show + 1);
    const pages = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const regPageNumbers = makePageNumbers(regTotalPages, regCurrentPage);
  const leadsPageNumbers = makePageNumbers(leadsTotalPages, leadsCurrentPage);

  // ---- Handlers ----
  const handleTabChange = (_e, value) => {
    setActiveTab(value);
    if (value === 'registrations') {
      setRegCurrentPage(1);
    } else {
      setLeadsCurrentPage(1);
    }
  };

  const handleRegSort = () => {
    setRegSortingOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
  };

  const handleRegSearch = (e) => {
    setRegSearchQuery(e.target.value);
    setRegCurrentPage(1);
  };
  return (
    <div className={classes.root}>
      {user ? ( // Conditionally render the user details table if the user is logged in
        <>
          {/* Tabs */}
          <Box className={classes.tabsWrap}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab value="registrations" label="Registrations" />
              <Tab value="leads" label="Leads" />
            </Tabs>
          </Box>

          {/* Search only for Registrations */}
          {activeTab === 'registrations' && (
            <TextField
              label="Search registrations"
              variant="outlined"
              value={regSearchQuery}
              onChange={handleRegSearch}
              style={{ marginBottom: '20px' }}
            />
          )}

          {user && activeTab === 'leads' && (
            <div className={classes.actionsRow}>
              <Button
                variant="contained"
                color="primary"
                className={classes.button}
                onClick={handleDownloadLeadsCSV}
              >
                Download CSV
              </Button>
            </div>
          )}

          {/* Table */}
          <TableContainer component={Paper} className={classes.tableContainer}>
            <div style={{ maxHeight: '500px' }}>
              <Table>
                <TableHead>
                  {activeTab === 'registrations' ? (
                    <TableRow>
                      <TableCell className={classes.tableHeaderCell}>
                        S.No
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>
                        ID
                      </TableCell>
                      <TableCell
                        onClick={handleRegSort}
                        style={{ cursor: 'pointer' }}
                        className={classes.tableHeaderCell}
                      >
                        Registered On
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>
                        First Name
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>
                        Family Name
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>
                        Company Name
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>
                        Country Code
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>
                        Mobile No
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>
                        Email
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>
                        City
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>
                        Nationality
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>
                        Job Title
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>
                        Department
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>
                        Business Nature of Company
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>
                        Your Current Industry
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>
                        Category
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>
                        Products
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>
                        Visit Purpose
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>
                        Show Info
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>
                        Interested
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow>
                      <TableCell className={classes.tableHeaderCell}>
                        S.No
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>
                        ID
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>
                        Created
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>
                        Email
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>
                        Phone
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>
                        Intent
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>
                        Consent
                      </TableCell>
                    </TableRow>
                  )}
                </TableHead>

                <TableBody>
                  {activeTab === 'registrations' ? (
                    sortedRegData?.length > 0 ? (
                      sortedRegData.map((data, index) => (
                        <TableRow key={data.id}>
                          <TableCell>
                            {index + 1 + (regCurrentPage - 1) * regRowsPerPage}
                          </TableCell>
                          <TableCell>
                            <a
                              href={`https://register.internationalapparelandtextilefair.com/getuserdetails?userid=${data.id}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {data.id}
                            </a>
                          </TableCell>
                          <TableCell>
                            {formatDateTime(data.createdAt)}
                          </TableCell>
                          <TableCell>{data.firstName}</TableCell>
                          <TableCell>{data.lastName}</TableCell>
                          <TableCell>{data.companyName}</TableCell>
                          <TableCell>{data?.mobileCountryCode}</TableCell>
                          <TableCell>{data.mobileNo}</TableCell>
                          <TableCell>{data.email}</TableCell>
                          <TableCell>{data.city}</TableCell>
                          <TableCell>
                            {data?.nationality ? data?.nationality : '---'}
                          </TableCell>
                          <TableCell>{data.jobTitle}</TableCell>
                          <TableCell>{data.department}</TableCell>
                          <TableCell title={data.businessNature}>
                            {data.businessNature}
                          </TableCell>
                          <TableCell>{data.industry}</TableCell>
                          <TableCell>
                            {Array.isArray(data.category)
                              ? data.category.join(', ')
                              : data.category}
                          </TableCell>
                          <TableCell title={data.products}>
                            {Array.isArray(data.products)
                              ? data.products.join(', ')
                              : data.products}
                          </TableCell>
                          <TableCell>
                            {Array.isArray(data.visitPurpose)
                              ? data.visitPurpose.join(', ')
                              : data.visitPurpose}
                          </TableCell>
                          <TableCell>{data.showInfo}</TableCell>
                          <TableCell>
                            {data.isInterested === 1 ? 'Yes' : 'No'}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={20} align="center">
                          No data available
                        </TableCell>
                      </TableRow>
                    )
                  ) : leadsLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        Loading…
                      </TableCell>
                    </TableRow>
                  ) : (leadsPageSlice || []).length > 0 ? (
                    (leadsPageSlice || []).map((lead, index) => (
                      <TableRow key={lead.id || `${lead.email}-${index}`}>
                        <TableCell>
                          {index +
                            1 +
                            (leadsCurrentPage - 1) * leadsRowsPerPage}
                        </TableCell>
                        <TableCell>{lead.id || '—'}</TableCell>
                        <TableCell>
                          {lead.createdAt ? formatDateTime(lead.createdAt) : '—'}
                        </TableCell>
                        <TableCell>{lead.email}</TableCell>
                        <TableCell>{lead.phone}</TableCell>
                        <TableCell>{lead.intent}</TableCell>
                        <TableCell>{lead.consent ? 'Yes' : 'No'}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No leads found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TableContainer>
          {/* Pagination */}
          <div className={classes.buttonContainer}>
            {activeTab === 'registrations' ? (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  className={classes.button}
                  onClick={() => setRegCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={regCurrentPage === 1}
                >
                  <span style={{ fontSize: '18px' }}>&#60;&#60;</span>
                </Button>
                {regPageNumbers.map((number) => (
                  <Button
                    key={`reg-${number}`}
                    variant="contained"
                    color="primary"
                    size="medium"
                    className={
                      number === regCurrentPage
                        ? classes.selectedPageNumber
                        : classes.pageNumber
                    }
                    onClick={() => setRegCurrentPage(number)}
                  >
                    {number}
                  </Button>
                ))}
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  className={classes.button}
                  onClick={() =>
                    setRegCurrentPage((p) => Math.min(regTotalPages, p + 1))
                  }
                  disabled={regCurrentPage >= regTotalPages}
                >
                  <span style={{ fontSize: '18px' }}>&#62;&#62;</span>
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  className={classes.button}
                  onClick={() => setLeadsCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={leadsCurrentPage === 1}
                >
                  <span style={{ fontSize: '18px' }}>&#60;&#60;</span>
                </Button>
                {leadsPageNumbers.map((number) => (
                  <Button
                    key={`leads-${number}`}
                    variant="contained"
                    color="primary"
                    size="medium"
                    className={
                      number === leadsCurrentPage
                        ? classes.selectedPageNumber
                        : classes.pageNumber
                    }
                    onClick={() => setLeadsCurrentPage(number)}
                  >
                    {number}
                  </Button>
                ))}
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  className={classes.button}
                  onClick={() =>
                    setLeadsCurrentPage((p) => Math.min(leadsTotalPages, p + 1))
                  }
                  disabled={leadsCurrentPage >= leadsTotalPages}
                >
                  <span style={{ fontSize: '18px' }}>&#62;&#62;</span>
                </Button>
              </>
            )}
          </div>
        </>
      ) : (
        // Render the register for event button and welcome message if the user is not logged in
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <Typography variant="h6" align="center" gutterBottom style={{ marginTop: '10px' }}>
            We are back with an all new sourcing experience with more than 400+
            exhibitors, over a span of 3 days
          </Typography>
          <Typography variant="h5" align="center" gutterBottom>
            16-17-18 November 2026
          </Typography>
          <Typography variant="h6" align="center" gutterBottom>
            <strong>DUBAI WORLD TRADE CENTRE</strong>
          </Typography>
          <Typography variant="subtitle1" align="center" gutterBottom>
            <strong>CONNECT - NETWORK - SOURCE</strong>
          </Typography>
          <div className={classes.buttonContainer}>
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
              <Button
                component={Link}
                to="/form"
                variant="contained"
                color="primary"
                size="large"
                className={classes.button}
              >
                REGISTER FOR THE EVENT
              </Button>
            </motion.div>
          </div>
          <div style={{ marginTop: '25px' }}>
            {/* <Typography variant="subtitle1" align="center" gutterBottom>
              <strong>Halls 6 and 7</strong>
            </Typography> */}
            <Typography variant="subtitle1" align="center" gutterBottom>
              <strong>10 AM - 6 PM</strong>
            </Typography>
            <Typography
              variant="subtitle1"
              align="center"
              gutterBottom
              style={{ color: '#ff0011' }}
            >
              <strong>FREE ENTRY</strong>
            </Typography>
            <Typography
              variant="subtitle1"
              align="center"
              gutterBottom
              style={{ color: '#ff0011' }}
            >
              <strong>AMPLE PAID PARKING AVAILABLE</strong>
            </Typography>
            <Typography
              variant="subtitle1"
              align="center"
              gutterBottom
              style={{ color: '#ff0011' }}
            >
              <strong>EXCLUSIVE BUYERS LOUNGE</strong>
            </Typography>
          </div>
          <div style={{ marginTop: '30px' }}>
            <Typography variant="subtitle1" align="center" gutterBottom>
              <strong>Notice for Visitors</strong>
            </Typography>
            <Typography
              variant="subtitle2"
              align="justify"
              gutterBottom
              style={{ maxWidth: '800px', margin: 'auto', padding: '10px' }}
            >
              To maintain the integrity and professionalism of the International
              Apparel & Textile Fair as a premier B2B platform, we kindly
              request all visitors to refrain from bringing large bags of
              samples into the venue. This policy ensures a focused and fair
              environment for all exhibitors and participants. Should visitors
              or potential buyers wish to present samples to exhibitors and
              manufacturers, we encourage them to arrange a meeting outside the
              fair’s operating hours, where sample exchanges can take place in a
              more suitable setting. We appreciate your understanding and
              cooperation in upholding the spirit of the event. <br />
              <br />
              <span style={{ color: 'red' }}>
                Please note that children under 16 are strictly not allowed in
                the exhibition halls.
              </span>
              <br />
              <br /> Thank you for your understanding and cooperation.
            </Typography>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default HomePage;
